import { PERMISSIONS } from './../../../services/permissions/permissions.service';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import Swal from 'sweetalert2';

import { AppsEditorOutput } from './apps-editor-output';
import { AppsEditorProfileField } from './apps-editor-profile-field';

import { App as AppInterface, ProfileFieldsOutput, ProgramsList, ProgramsService } from '../../../users-api';
import { Observable } from 'rxjs';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { LockRedemptionComponent } from '../lock-redemption/lock-redemption.component';

@Component({
  selector: 'app-apps-editor',
  templateUrl: './apps-editor.component.html',
  styleUrls: ['./apps-editor.component.scss'],
  providers: [
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
  ],
})
export class AppsEditorComponent implements OnInit {
  @ViewChild('lock_redemption', { static: true }) lock_redemption: LockRedemptionComponent;
  @Input() appData: AppInterface = {
    name: '',
    id: '',
    pwa_domain: '',
    login_register: {
      log_in: { method: '' as any },
      register: { method: '' as any },
    },
    gcp: {
      main_project_id: '',
      recaptcha_auth: false,
      recaptcha_secret: '',
      recaptcha_enterprise: false,
      recaptcha_min_score: 0.5,
      recaptcha_keys_ids: [],
      custom_token_signer: false,
    },
    auth_jwt_info: {
      issuer: '',
      audiences: [],
      jwks_uri: '',
    },
    puntored_recaudo: {
      factor: 0,
      offset_cents: 0
    },
    card_terminal: {
      factor: 0,
      offset_cents: 0
    },
    register_type: '',
    twilio: {
      verify_sid: '',
      token: '',
      sid: '',
    },
    short_lived_tokens: false,
    expire_elastic_token: false,
    verify_recovery_info: false,
    enumeration_protection: false,
    enabled: true,
  };

  @Input() profileFields: AppsEditorProfileField[] = [];
  @Input() mode = 'create';

  @Output() onOK = new EventEmitter<AppsEditorOutput>();
  @Output() onCancel = new EventEmitter<void>();

  disable_register_type = false;
  uriSeparatorKeyCodes = [ENTER, COMMA];
  readonly searchApiUrlPattern =
    /^https:\/\/[^\/\s]+\/v1\/front\/search-data\/app\/[^\/\s]+\/index\/[^\/\s]+(?:[\/?#].*)?$/;

  programsOptions: string[] = [];
  filteredProgramsOptions: Observable<string[]>;

  appProfileDefinition: ProfileFieldsOutput;
  // appProfileFields: Array<AppUsersEditorFieldData> = [];
  loadingAppProfileDefinition = false;
  programIdsByAppMap = new Map<string, string[]>();
  programInputControl = new FormControl();
  recaptchaKeysWithCommas = '';
  PERMISSIONS = PERMISSIONS;
  constructor(private snackBar: MatSnackBar, private programsApi: ProgramsService) { }

  get is_deactivated(): boolean {
    return !this.appData.enabled;
  }
  set is_deactivated(value: boolean) {
    this.appData.enabled = !value;
  }

  async ngOnInit() {
    this.adjustAppData();
    if (this.mode === 'create') {
      this.appData.gcp.recaptcha_auth = true;
      this.appData.gcp.recaptcha_enterprise = true;
      this.appData.gcp.custom_token_signer = true;
      this.appData.verify_recovery_info = true;
      this.appData.enumeration_protection = true;
      this.appData.short_lived_tokens = true;
      this.appData.expire_elastic_token = true;
    }
    if (this.mode === 'edit') {
      if (this.appData.register_type) {
        //   this.disable_register_type = true;
      }
    }
    this.loadPrograms();
    // Filter programs
    this.filteredProgramsOptions = this.programInputControl.valueChanges
      .pipe(startWith(null))
      .pipe(
        map((val) =>
          val
            ? this.filtersAutoComplete(val, 'programsOptions')
            : this.programsOptions.slice()
        )
      );
    if (this.appData.twilio === undefined) {
      this.appData.twilio = {
        verify_sid: '',
        token: '',
        sid: '',
      };
    }
  }

  adjustAppData() {
    if (!this.appData) {
      return;
    }

    this.appData.pwa_domain = this.appData.pwa_domain || '';

    // Initialize login_register if missing
    if (!this.appData.login_register) {
      this.appData.login_register = { log_in: { method: 'password' } };
    }
    if (!this.appData.login_register.log_in) {
      this.appData.login_register.log_in = { method: '' as any };
    }

    // Migrate legacy top-level label fields into login_register
    const legacyFields: Array<'account_label' | 'register_code_label' | 'iddoc_label' | 'iddoc_type_label' | 'iddoc_type_options'> =
      ['account_label', 'register_code_label', 'iddoc_label', 'iddoc_type_label', 'iddoc_type_options'];
    for (const field of legacyFields) {
      if (this.appData[field] !== undefined && this.appData.login_register[field] === undefined) {
        (this.appData.login_register as any)[field] = this.appData[field];
      }
    }

    // Initialize iddoc_type_options array if missing
    if (!this.appData.login_register.iddoc_type_options) {
      this.appData.login_register.iddoc_type_options = [];
    }

    // Initialize session_expiration if missing
    if (!this.appData.login_register.session_expiration) {
      this.appData.login_register.session_expiration = { enable: false };
    }

    // Always ensure register exists
    if (!this.appData.login_register.register) {
      this.appData.login_register.register = { method: '' as any };
    }

    // Initialize saml/openid objects based on selected method
    if (this.appData.login_register.log_in.method === 'saml' && !this.appData.login_register.log_in.saml) {
      this.appData.login_register.log_in.saml = { provider: '', label: '' };
    }
    if (this.appData.login_register.log_in.method === 'openid' && !this.appData.login_register.log_in.openid) {
      this.appData.login_register.log_in.openid = { provider: '', label: '' };
    }

    if (this.appData.gcp === undefined) {
      this.appData.gcp = {
        main_project_id: '',
        recaptcha_auth: false,
        recaptcha_secret: '',
        recaptcha_enterprise: false,
        recaptcha_keys_ids: [],
        recaptcha_min_score: 0.5,
        custom_token_signer: false,
      };
    }
    if (this.appData.gcp.recaptcha_enterprise === undefined) {
      this.appData.gcp.recaptcha_enterprise = false;
      this.appData.gcp.recaptcha_keys_ids = [];
      this.appData.gcp.recaptcha_min_score = 0.5;
    }
    if (this.appData.gcp && this.appData.gcp.recaptcha_keys_ids) {
      this.recaptchaKeysWithCommas =
        this.appData.gcp.recaptcha_keys_ids.join(',');
    }
    if (this.appData.puntored_recaudo === undefined) {
      this.appData.puntored_recaudo = {
        factor: 0,
        offset_cents: 0
      };
    }
    if (this.appData.card_terminal === undefined) {
      this.appData.card_terminal = {
        factor: 0,
        offset_cents: 0
      };
    }
    if (this.appData.enabled === undefined) {
      this.appData.enabled = true;
    }
  }

  async okClicked() {
    if (this.appData &&
      this.appData.gcp &&
      this.recaptchaKeysWithCommas) {
      this.appData.gcp.recaptcha_keys_ids = this.recaptchaKeysWithCommas
        .split(',')
        .map(key => key.trim());
    }
    if (this.appData &&
      this.appData.gcp && this.appData.gcp.recaptcha_auth) {
      if (!this.appData.gcp.recaptcha_enterprise &&
        !this.appData.gcp.recaptcha_secret) {
        alert('Se debe agregar el Recaptcha Secret.');
        return;
      }
      if (this.appData.gcp.recaptcha_enterprise) {
        if (!this.appData.gcp.recaptcha_keys_ids.length) {
          alert('Se deben agregar Recaptcha Key IDs.');
          return;
        }
        if (this.appData.gcp.recaptcha_min_score) {
          this.appData.gcp.recaptcha_min_score =
            parseFloat(this.appData.gcp.recaptcha_min_score as any);
        }
        if (this.appData.gcp.recaptcha_min_score === undefined ||
          isNaN(this.appData.gcp.recaptcha_min_score) ||
          this.appData.gcp.recaptcha_min_score < 0 ||
          this.appData.gcp.recaptcha_min_score > 1) {
          alert('Recaptha Puntaje Mínimo debe ser un número entre 0.0 y 1.0');
          return;
        }
      }
    }

    const fieldWithHelpMissingText = this.profileFields.find(f => f.help && !f.help_text?.trim());
    if (fieldWithHelpMissingText) {
      Swal.fire({
        title: `El campo "${fieldWithHelpMissingText.title || fieldWithHelpMissingText.id}" tiene "Ayuda" activo pero no tiene texto de ayuda. Por favor escribe el texto o desactiva la ayuda.`,
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    const fieldWithInvalidSearchApiUrl = this.profileFields.find(
      f => f.validation_type === 'search_api' &&
        !this.searchApiUrlPattern.test((f.search_api_url || '').trim())
    );
    if (fieldWithInvalidSearchApiUrl) {
      Swal.fire({
        title: 'URL de Search Api inválida',
        text: `El campo "${fieldWithInvalidSearchApiUrl.title || fieldWithInvalidSearchApiUrl.id}" debe tener una URL HTTPS con la ruta /v1/front/search-data/app/{appId}/index/{indexName}.`,
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (!this.validateTwilioFields()) {
      Swal.fire({
        title: 'Todos los campos de Twilio son obligatorios.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (this.appData.twilio) {
      if (!this.appData.twilio.verify_sid && !this.appData.twilio.token && !this.appData.twilio.sid) {
        delete this.appData.twilio;
      }
    }

    // Clean up top-level pwa_domain if empty
    if (this.appData.pwa_domain === '' || this.appData.pwa_domain === null) {
      delete this.appData.pwa_domain;
    }

    // Remove legacy top-level label fields (now in login_register)
    const legacyLabelFields = ['account_label', 'register_code_label', 'iddoc_label', 'iddoc_type_label', 'iddoc_type_options'];
    for (const field of legacyLabelFields) {
      delete this.appData[field];
    }

    // Clean up login_register
    if (this.appData.login_register) {
      const lr = this.appData.login_register;

      // Validate login method is selected
      if (!lr.log_in?.method) {
        this.snackBar.open('El método de login es obligatorio.', 'Cerrar', { duration: 5000 });
        return;
      }

      // Validate register method is selected
      if (!lr.register?.method) {
        this.snackBar.open('El método de registro es obligatorio.', 'Cerrar', { duration: 5000 });
        return;
      }

      // Eliminar account_search_field cuando no aplica
      if (lr.log_in?.method !== 'password' && lr.register?.method !== 'code') {
        delete lr.account_search_field;
      }

      // Validate account_search_field when required
      if ((lr.log_in?.method === 'password' || lr.register?.method === 'code') && !lr.account_search_field) {
        this.snackBar.open('El campo de búsqueda de cuenta es obligatorio cuando la autenticación es contraseña o el registro es por código de invitación.', 'Cerrar', { duration: 6000 });
        return;
      }

      // Validate SAML required fields
      if (lr.log_in?.saml) {
        if (!lr.log_in.saml.provider || !lr.log_in.saml.label) {
          this.snackBar.open('Los campos Provider y Label de SAML son requeridos.', 'Cerrar', { duration: 5000 });
          return;
        }
        if (!lr.log_in.saml.description) {
          delete lr.log_in.saml.description;
        }
      }

      // Validate OpenID required fields
      if (lr.log_in?.openid) {
        if (!lr.log_in.openid.provider || !lr.log_in.openid.label) {
          this.snackBar.open('Los campos Provider y Label de OpenID son requeridos.', 'Cerrar', { duration: 5000 });
          return;
        }
        if (!lr.log_in.openid.description) {
          delete lr.log_in.openid.description;
        }
      }

      // Borrar register_code_label si el método de registro no es 'code'
      if (lr.register?.method !== 'code') {
        delete lr.register_code_label;
      }

      // Clean up empty string label fields inside login_register
      const lrStringFields: Array<'account_label' | 'register_code_label' | 'iddoc_label' | 'iddoc_type_label'> =
        ['account_label', 'register_code_label', 'iddoc_label', 'iddoc_type_label'];
      for (const field of lrStringFields) {
        if (lr[field] === '' || lr[field] === null) {
          delete lr[field];
        }
      }
      if (lr.iddoc_type_options && lr.iddoc_type_options.length === 0) {
        delete lr.iddoc_type_options;
      }
      if (lr.account_search_field === undefined || (lr.account_search_field as any) === '') {
        delete lr.account_search_field;
      }

      // Validar expiración de sesión: si está activa se requiere un entero positivo de segundos
      if (lr.session_expiration?.enable) {
        const seconds = lr.session_expiration.inactivity_timeout_seconds;
        if (!Number.isInteger(seconds) || seconds < 1) {
          this.snackBar.open('Los segundos de inactividad deben ser un número entero mayor a 0 cuando la expiración de sesión está activa.', 'Cerrar', { duration: 6000 });
          return;
        }
      } else if (lr.session_expiration) {
        lr.session_expiration = { enable: false };
      }

      // No enviar google: false, solo enviar cuando está activo
      if (!lr.log_in?.google) {
        delete (lr.log_in as any).google;
      }
      if (!lr.register?.google) {
        delete (lr.register as any).google;
      }

      // preferred_recovery_method solo aplica con registro por código; vacío = ambos (no se envía)
      if (lr.register?.method !== 'code' || !lr.register?.preferred_recovery_method) {
        delete lr.register?.preferred_recovery_method;
      }
    }

    const output = new AppsEditorOutput(this.appData, this.profileFields, { remove_missing_fields: true });

    this.onOK.emit(output);
    return false;
  }

  async saveLockRedemption(): Promise<boolean> {
    return this.lock_redemption.save();
  }

  cancelClicked() {
    this.onCancel.emit();
    return false;
  }

  addProfileField() {
    const newField: AppsEditorProfileField = {
      id: '',
      title: '',
      pattern: '',
      format: '',
      enum: [],
      search_api_url: '',
      use_in_auth: false,
      public: false,
      validation_type: 'free',
      self_managed: false,
      self_managed_programs: [],
      autocomplete_url: '',
      required: false,
      help: false,
      help_text: '',
      show_in_overview: false,
    };
    this.profileFields.push(newField);
  }

  removeProfileField(field) {
    const index = this.profileFields.indexOf(field);
    if (index >= 0) {
      this.profileFields.splice(index, 1);
    }
  }

  addFieldEnumItem(event: MatChipInputEvent, field) {
    const value = event.value.trim();
    const input = event.input;

    if (value && input) {
      field.enum.push(value);
      input.value = '';
    }
  }

  addAudiences(event: MatChipInputEvent) {
    const input = event.input;
    const value = event.value.trim();

    if (value && input) {
      this.appData.auth_jwt_info.audiences.push(value);
      input.value = '';
    }
  }

  removeAudience(index: number) {
    this.appData.auth_jwt_info.audiences.splice(index, 1);
  }

  setMainGCPProjectId(main_project_id: string) {
    if (!this.appData.gcp) {
      this.appData.gcp = {
        main_project_id,
        recaptcha_auth: false,
        recaptcha_secret: '',
        recaptcha_enterprise: false,
        recaptcha_keys_ids: [],
        recaptcha_min_score: 0.5,
        custom_token_signer: false,
      };
    }
    this.appData.gcp.main_project_id = main_project_id;
  }

  jwtInfoFromMainGCPProject() {
    const id = this.appData
      && this.appData.gcp
      && this.appData.gcp.main_project_id;
    if (!id) {
      return;
    }
    this.appData.auth_jwt_info = {
      issuer: `https://securetoken.google.com/${id}`,
      audiences: [id],
      jwks_uri: 'https://www.googleapis.com/robot/v1/metadata/x509/securetoken@system.gserviceaccount.com',
    };
  }

  filtersAutoComplete(value: string, type: string) {
    console.log('filtersAutoComplete', value, type);

    return this[type].filter(
      (option: string) =>
        option && option.toLowerCase().indexOf(value.toLowerCase()) >= 0
    );
  }

  async loadPrograms() {
    if (!this.appData.id) {
      return;
    }
    let ids = this.programIdsByAppMap.get(this.appData.id);
    if (ids) {
      this.programsOptions = ids;
      this.programInputControl.setValue('', { emitEvent: true });
      return;
    }
    this.programIdsByAppMap.set(this.appData.id, []);
    const programsList: ProgramsList =
      await this.programsApi.findAppIdPrograms(this.appData.id).toPromise();
    if (!programsList.data) {
      return;
    }
    ids = programsList.data.map((program) => program.id);
    this.programsOptions = ids.map((program) => program.split('/').slice(-1).pop());
    this.programIdsByAppMap.set(this.appData.id, ids);
    this.programInputControl.setValue('', { emitEvent: true });
  }

  addProgram(event: MatChipInputEvent, index?) {
    const input = event.input;
    const value = event.value.trim();

    if (value && input) {
      // Check input program is known
      if (this.programsOptions.indexOf(value) < 0) {
        return;
      }
      // Init property if non existent
      if (!this.profileFields[index].self_managed_programs) {
        this.profileFields[index].self_managed_programs = [];
      }
      // Check value is not repeated
      if (this.profileFields[index].self_managed_programs.indexOf(value) >= 0) {
        return;
      }
      // Add value

      this.profileFields[index].self_managed_programs.push(value);
      input.value = '';
    }
  }

  removeProgram(program: string, index_field, index_program) {
    if (index_program >= 0) {
      this.profileFields[index_field].self_managed_programs.splice(index_program, 1);
    }
  }

  validateTwilioFields(): boolean {
    if (!this.appData.twilio) {
      return true;
    }

    const { verify_sid, token, sid } = this.appData.twilio;

    const isAtLeastOneFilled = !!verify_sid || !!token || !!sid;
    const areAllFilled = !!verify_sid && !!token && !!sid;

    return !isAtLeastOneFilled || areAllFilled;
  }

  addIdDocTypeOption(event: any): void {
    const input = event.input;
    const value = (event.value || '').trim();

    if (value) {
      if (!this.appData.iddoc_type_options) {
        this.appData.iddoc_type_options = [];
      }

      if (this.appData.iddoc_type_options.indexOf(value) === -1) {
        this.appData.iddoc_type_options.push(value);
      }
    }

    if (input) {
      input.value = '';
    }
  }

  onRegisterMethodChange(method: string) {
    if (method !== 'code' && this.appData.login_register) {
      delete this.appData.login_register.register_code_label;
      delete this.appData.login_register.register?.preferred_recovery_method;
    }
  }

  onLoginMethodChange(method: string) {
    if (method === 'saml' && !this.appData.login_register.log_in.saml) {
      this.appData.login_register.log_in.saml = { provider: '', label: '' };
    }
    if (method === 'openid' && !this.appData.login_register.log_in.openid) {
      this.appData.login_register.log_in.openid = { provider: '', label: '' };
    }
    // No se borran saml/openid al cambiar el método principal,
    // ya que pueden estar configurados como métodos adicionales.
  }

  toggleSamlAdditional(checked: boolean) {
    if (checked) {
      if (!this.appData.login_register.log_in.saml) {
        this.appData.login_register.log_in.saml = { provider: '', label: '' };
      }
    } else {
      delete this.appData.login_register.log_in.saml;
    }
  }

  toggleOpenidAdditional(checked: boolean) {
    if (checked) {
      if (!this.appData.login_register.log_in.openid) {
        this.appData.login_register.log_in.openid = { provider: '', label: '' };
      }
    } else {
      delete this.appData.login_register.log_in.openid;
    }
  }

  addLoginRegisterIdDocTypeOption(event: any): void {
    const input = event.input;
    const value = (event.value || '').trim();

    if (value) {
      if (!this.appData.login_register.iddoc_type_options) {
        this.appData.login_register.iddoc_type_options = [];
      }
      if (this.appData.login_register.iddoc_type_options.indexOf(value) === -1) {
        this.appData.login_register.iddoc_type_options.push(value);
      }
    }
    if (input) {
      input.value = '';
    }
  }
}
